import {
  intranetAcknowledgementReportSchema,
  intranetContentDetailSchema,
  intranetOwnAcknowledgementsResponseSchema,
  intranetPendingAcknowledgementsResponseSchema,
  type CreateIntranetCommentWithFileRequest,
  type IntranetReactionKind,
  type LockIntranetCommentsRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import {
  intranetContentKey,
  intranetContentsKey,
  intranetQueryKey,
} from '@/products/intranet/features/intranet/use-intranet';

export const intranetPendingAckKey = [...intranetQueryKey, 'pending-ack'] as const;

export function intranetAcknowledgementsKey(slug: string | undefined) {
  return [...intranetQueryKey, 'acknowledgements', slug] as const;
}

export function usePendingIntranetAcknowledgements() {
  return useQuery({
    queryKey: intranetPendingAckKey,
    queryFn: () =>
      apiFetch('/intranet/acknowledgements/pending', intranetPendingAcknowledgementsResponseSchema),
  });
}

export function useIntranetOwnAcknowledgements(enabled: boolean) {
  return useQuery({
    queryKey: [...intranetQueryKey, 'acknowledgements', 'me'],
    queryFn: () =>
      apiFetch('/intranet/acknowledgements/me', intranetOwnAcknowledgementsResponseSchema),
    enabled,
  });
}

export function useIntranetAcknowledgementReport(slug: string | undefined) {
  return useQuery({
    queryKey: intranetAcknowledgementsKey(slug),
    queryFn: () =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug ?? '')}/acknowledgements`,
        intranetAcknowledgementReportSchema,
      ),
    enabled: Boolean(slug),
  });
}

function useInvalidateAck(slug: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: intranetContentKey(slug) });
    void queryClient.invalidateQueries({ queryKey: intranetContentsKey });
    void queryClient.invalidateQueries({ queryKey: intranetPendingAckKey });
    void queryClient.invalidateQueries({ queryKey: intranetAcknowledgementsKey(slug) });
  };
}

export function useAcknowledgeIntranetContent(slug: string) {
  const invalidate = useInvalidateAck(slug);
  return useMutation({
    mutationFn: () =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/acknowledge`,
        intranetContentDetailSchema,
        { method: 'POST' },
      ),
    onSuccess: invalidate,
  });
}

export function useAddIntranetComment(slug: string) {
  const invalidate = useInvalidateAck(slug);
  return useMutation({
    mutationFn: (input: CreateIntranetCommentWithFileRequest & { file?: File }) => {
      if (input.file) {
        const form = new FormData();
        form.set('body', input.body ?? '');
        if (input.parentId) {
          form.set('parentId', input.parentId);
        }
        if (input.mentionUserIds && input.mentionUserIds.length > 0) {
          form.set('mentionUserIds', JSON.stringify(input.mentionUserIds));
        }
        form.set('file', input.file);
        return apiFetch(
          `/intranet/contents/${encodeURIComponent(slug)}/comments`,
          intranetContentDetailSchema,
          { method: 'POST', body: form },
        );
      }
      return apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/comments`,
        intranetContentDetailSchema,
        {
          method: 'POST',
          body: JSON.stringify({
            body: input.body,
            ...(input.parentId ? { parentId: input.parentId } : {}),
            ...(input.mentionUserIds && input.mentionUserIds.length > 0
              ? { mentionUserIds: input.mentionUserIds }
              : {}),
          }),
        },
      );
    },
    onSuccess: invalidate,
  });
}

export function useRemoveIntranetComment(slug: string) {
  const invalidate = useInvalidateAck(slug);
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/intranet/comments/${encodeURIComponent(id)}`, intranetContentDetailSchema, {
        method: 'DELETE',
      }),
    onSuccess: invalidate,
  });
}

/** One reaction on a comment. The response is the post, so the thread reloads. */
export function useSetIntranetCommentReaction(slug: string) {
  const invalidate = useInvalidateAck(slug);
  return useMutation({
    mutationFn: ({ id, reaction }: { id: string; reaction: IntranetReactionKind }) =>
      apiFetch(
        `/intranet/comments/${encodeURIComponent(id)}/reactions`,
        intranetContentDetailSchema,
        { method: 'POST', body: JSON.stringify({ reaction }) },
      ),
    onSuccess: invalidate,
  });
}

/** Like toggle for one comment. The post like stays on `/contents/:slug/reactions`. */
export function useSetIntranetCommentLike(slug: string) {
  const invalidate = useInvalidateAck(slug);
  return useMutation({
    mutationFn: ({ id, liked }: { id: string; liked: boolean }) =>
      apiFetch(
        `/intranet/comments/${encodeURIComponent(id)}/reactions`,
        intranetContentDetailSchema,
        { method: liked ? 'POST' : 'DELETE' },
      ),
    onSuccess: invalidate,
  });
}

export function useLockIntranetComments(slug: string) {
  const invalidate = useInvalidateAck(slug);
  return useMutation({
    mutationFn: (body: LockIntranetCommentsRequest) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/lock-comments`,
        intranetContentDetailSchema,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    onSuccess: invalidate,
  });
}
